import {useMemo, useState} from 'react';
import type {SetRequired} from 'type-fest';
import {
	create,
	type UseBoundStore,
	type StoreApi,
	type StateCreator,
} from 'zustand';
import type {ReactAction} from '@netx/core/bootstrap';

// TODO: document and move to core-components

type MenuItem = {
	action: ReactAction;
	section: string;
	subSection?: string;
};

type MenuStateOptions = {
	section?: string;
	subSection?: string;
};

type MenuState = {
	menuItems: MenuItem[];
	addMenuItems: (
		items: ReactAction[],
		opts: SetRequired<MenuStateOptions, 'section'>,
	) => void;
	removeSection: (section: string) => void;
	removeSubSection: (section: string, subSection: string) => void;
	removeMenuItems: (items: ReactAction[], opts?: MenuStateOptions) => void;
	updateMenuItems: (
		items: ReactAction[],
		opts: SetRequired<MenuStateOptions, 'section'>,
	) => void;
};

const menuStateCreator: StateCreator<MenuState, [], []> = (set, get) => ({
	menuItems: [],
	addMenuItems: (actions, {section, subSection}) =>
		set((state) => ({
			menuItems: [
				...state.menuItems,
				...actions.map((action) => ({section, subSection, action})),
			],
		})),
	removeSection: (section) =>
		set((state) => ({
			menuItems: state.menuItems.filter((item) => item.section !== section),
		})),
	removeSubSection: (section, subSection) =>
		set((state) => ({
			menuItems: state.menuItems.filter(
				(item) => item.section !== section || item.subSection !== subSection,
			),
		})),
	removeMenuItems: (actions, {section, subSection} = {}) =>
		set((state) => {
			const names = actions.map((action) => action.name);
			return {
				menuItems: state.menuItems.filter((item) => {
					if (section && item.section !== section) {
						if (!subSection || item.subSection !== subSection) return true;
					}
					return !names.includes(item.action.name);
				}),
			};
		}),
	// TODO: cleanup
	updateMenuItems: (actions, {section, subSection}) =>
		set((state) => {
			let updatedMenuItems = [...state.menuItems];

			if (section || subSection) {
				// eliminate items that match the section and/or subSection
				updatedMenuItems = updatedMenuItems.filter((item) => {
					if (section && item.section === section) {
						if (!subSection || item.subSection === subSection) return false;
					}
					return true;
				});
				// add the new actions
				actions.forEach((action) => {
					updatedMenuItems.push({section, subSection, action});
				});
			} else {
				actions.forEach((action) => {
					const item = updatedMenuItems.find(
						(item) => item.action.name === action.name,
					);
					if (item) {
						const index = updatedMenuItems.indexOf(item);
						updatedMenuItems.splice(index, 1, {...item, action});
					} else {
						updatedMenuItems.push({action, section});
					}
				});
			}

			return {
				menuItems: updatedMenuItems,
			};
		}),
});

export const createMenuStore = () => create<MenuState>()(menuStateCreator);

function filterMenuActions(action: ReactAction): ReactAction | null {
	if (action.enableTest?.() === false) return null;

	if (action.children) {
		const filteredChildren = action.children
			.map((child) => {
				if (Array.isArray(child)) {
					return child
						.map(filterMenuActions)
						.filter((child): child is ReactAction => child !== null);
				} else {
					return filterMenuActions(child);
				}
			})
			.filter(
				(child): child is ReactAction | ReactAction[] =>
					child !== null && (Array.isArray(child) ? child.length > 0 : true),
			);

		if (filteredChildren.length === 0) return null;

		return {
			...action,
			children: filteredChildren as ReactAction[],
		};
	}

	return action;
}

function filterMenuItems(items: MenuItem[]): MenuItem[] {
	return items
		.map((item) => {
			const filteredAction = filterMenuActions(item.action);
			return filteredAction ? {...item, action: filteredAction} : null;
		})
		.filter((item): item is MenuItem => item !== null);
}

type UseMenuParams = {
	store: UseBoundStore<StoreApi<MenuState>>;
};

export const useMenu = ({store}: UseMenuParams) => {
	const [filter, setFilter] = useState('');

	const menuItems = store((state) => state.menuItems);

	const filteredMenuItems = useMemo(() => {
		if (filter === '') return menuItems;
		const _filter = filter.toLowerCase();

		const hasMatch = (action: ReactAction): boolean => {
			if (action.label.toLowerCase().includes(_filter)) return true;
			if (action.children) {
				return action.children.flat().some((child) => {
					if (Array.isArray(child))
						return child.some((child) => hasMatch(child));
					return hasMatch(child);
				});
			}
			return false;
		};

		return menuItems.filter((item) => hasMatch(item.action));
	}, [filter, menuItems]);

	const enabledMenuItems = filterMenuItems(filteredMenuItems);

	const actions = enabledMenuItems.map((item) => item.action);

	return {
		actions,
		enabledMenuItems,
		filter,
		filteredMenuItems,
		isFiltered: filter !== '',
		menuItems,

		setFilter,
	};
};
